import type { VehicleInterestAnswer } from "@hakili/contracts";
import { pl, t } from "@hakili/i18n";
import { Columns3, LayoutList, Phone } from "lucide-react";
import { ERROR_TEXT } from "@hakili/ui";
import { useCallback, useEffect, useState, type ReactElement } from "react";

import type { DealerDossiersReadAnswer } from "../session/dealer-dossiers.js";
import type { Inventory, DoorResult } from "../session/useSession.js";
import { dayOf, sinceWords } from "./dossier-dates.js";
import { Initials } from "./dossiers.js";
import { ReadFailure } from "./read-failure.js";

type Dossier = DealerDossiersReadAnswer["dossiers"][number];

/**
 * `P1-DLW-054` (PROSPECTS, R's batch 6 at `98e70c63`, frames 36 to 38; the architect's ruling of 2 November 2026): **the dealer's Prospects, on the « Demande de financement » column only.**
 *
 * A filed financing dossier IS a consented lead (D-1031-18): the dealer's dossiers door already serves the client's name and phone, so this screen reads the SAME door through the SAME reader (`readDealerDossiers`, lane E's) and writes
 * nothing. The interest column (`GET /api/v1/me/vehicles/{id}/interest`, `savedCount`) and the visit column (a read-only list) have no callable door: each says what will fill it and draws NO number, because a count the server did not
 * serve is never drawn and never « 0 ». Questions and the contact request outside a dossier are after the pilot: no column. No tile row (ruling). The rows have no « Voir le dossier » and the header no « Au comptoir »: the dealer has no
 * dossier page and Comptoir is not built, and a dead control is never drawn. « Appeler » is a `tel:` link on the served phone.
 */
export interface ProspectsProps {
  readonly readDossiers: () => Promise<DoorResult<DealerDossiersReadAnswer>>;
  /** `PROSPECTS-INTEREST`: the dealership's cars, and the per-car save count (`GET /me/vehicles/{id}/interest`). Both optional: without them the column keeps #931's own sentence. */
  readonly listVehicles?: () => Promise<DoorResult<Inventory>>;
  readonly readInterest?: (vehicleId: string) => Promise<DoorResult<VehicleInterestAnswer>>;
}

/** One published car and what its read answered: a served count, or the door's own sentence. Never a count the server did not serve. */
type InterestLine =
  | { readonly id: string; readonly title: string; readonly ok: true; readonly saved: number }
  | { readonly id: string; readonly title: string; readonly ok: false; readonly message: string };
type Interest =
  | { readonly state: "none" }
  | { readonly state: "refused"; readonly message: string }
  | { readonly state: "lines"; readonly lines: readonly InterestLine[] };

/**
 * **THE LEAD CUT (PROPOSED, to rule): a client's dossier whose stage is BEFORE the bank holds the file.** These are the dossier stage codes (`dossier.stage_definitions`) up to `pret`; from `soumis` on the file is the bank's and the
 * dealer's Dossiers screen's, not a prospect to call back. A dossier the dealership filed itself (`origin.code` `concession`) is its own client, no prospect either. The code decides the cut and is never printed.
 */
export const LEAD_STAGES: readonly string[] = ["intake", "expertise", "collecte", "analyse", "pret"];

const isLead = (dossier: Dossier): boolean =>
  dossier.origin.code === "client" && LEAD_STAGES.includes(dossier.stage);

const telOf = (phone: string): string => `tel:${phone.replace(/\s+/gu, "")}`;

export function Prospects(props: ProspectsProps): ReactElement {
  const { readDossiers } = props;
  const [read, setRead] = useState<DoorResult<DealerDossiersReadAnswer> | null>(null);
  const [today, setToday] = useState<string | null>(null);
  const [view, setView] = useState<"liste" | "colonnes">("liste");
  const [interest, setInterest] = useState<Interest>({ state: "none" });
  const { listVehicles, readInterest } = props;

  useEffect(() => {
    if (listVehicles === undefined || readInterest === undefined) return;
    let stopped = false;
    const live = (): boolean => !stopped;
    void (async () => {
      const cars = await listVehicles();
      if (!live()) return;
      if (!cars.ok) {
        setInterest({ state: "refused", message: cars.message });
        return;
      }
      const published = cars.value.vehicles.filter((car) => car.published);
      const lines = await Promise.all(
        published.map(async (car): Promise<InterestLine> => {
          const answer = await readInterest(car.id);
          return answer.ok
            ? { id: car.id, title: car.title, ok: true, saved: answer.value.savedCount }
            : { id: car.id, title: car.title, ok: false, message: answer.message };
        }),
      );
      if (live()) setInterest({ state: "lines", lines });
    })();
    return () => {
      stopped = true;
    };
  }, [listVehicles, readInterest]);

  const load = useCallback(async (): Promise<void> => {
    const answer = await readDossiers();
    // `OD-17`: the instant is the SERVER's, carried by the answer (`asOf`). An answer without it dates nothing here.
    setToday(answer.ok && answer.value.asOf !== undefined ? dayOf(answer.value.asOf) : null);
    setRead(answer);
  }, [readDossiers]);

  useEffect(() => {
    void load();
  }, [load]);

  const title = (
    <h1 className="text-2xl font-semibold text-gray-900" data-testid="dealer-prospects-titre">
      {t("dealer.prospects.titre")}
    </h1>
  );

  if (read === null) return <div data-testid="dealer-prospects-chargement" aria-busy="true" />;

  if (!read.ok) {
    return (
      <section>
        {title}
        <div className="mt-4">
          <ReadFailure
            what="dealer.lecture.dossiers"
            message={read.message}
            onRetry={() => {
              setRead(null);
              void load();
            }}
            testId="dealer-prospects-echec"
            retryTestId="dealer-prospects-echec-reessayer"
          />
        </div>
      </section>
    );
  }

  const leads = read.value.dossiers.filter(isLead);

  /** In a column the card is narrow: the call stands under the text (the drawing's own rule for a 290 px column), never beside it, and it carries no chip (R's review of #931: the chip repeats the column's title; the list keeps it). */
  const card = (dossier: Dossier, stacked: boolean): ReactElement => (
    <article
      key={dossier.id}
      className={`grid items-center gap-x-3 gap-y-2 rounded-2xl border border-gray-200 bg-white p-4 ${
        stacked ? "grid-cols-[2.5rem_minmax(0,1fr)]" : "grid-cols-[2.5rem_minmax(0,1fr)_auto]"
      }`}
      data-testid="dealer-prospects-ligne"
    >
      <Initials name={dossier.client.name} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <b className="text-sm text-gray-900" data-testid="dealer-prospects-nom">
            {dossier.client.name}
          </b>
          {stacked ? null : (
            <span className="whitespace-nowrap rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              {t("dealer.prospects.puceFinancement")}
            </span>
          )}
          <small className="text-xs text-gray-500">{sinceWords(dossier.since, today)}</small>
        </div>
        <p className="m-0 mt-1 text-sm text-gray-700">{dossier.status.whereText}</p>
        <small className="block text-xs text-gray-500">{dossier.vehicle.label}</small>
      </div>
      <a
        href={telOf(dossier.client.phone)}
        aria-label={t("dealer.prospects.appelerNom").split("{nom}").join(dossier.client.name)}
        data-testid="dealer-prospects-appeler"
        className={`inline-flex items-center gap-1.5 justify-self-start rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 ${
          stacked ? "col-span-2" : ""
        }`}
      >
        <Phone className="h-4 w-4" aria-hidden="true" />
        {t("dealer.prospects.appeler")}
      </a>
    </article>
  );

  const views = (
    <div
      role="radiogroup"
      aria-label={t("dealer.prospects.affichage")}
      className="inline-flex overflow-hidden rounded-xl border border-gray-200 bg-white text-sm font-medium"
    >
      <button
        type="button"
        role="radio"
        aria-checked={view === "liste"}
        data-testid="dealer-prospects-vue-liste"
        onClick={() => {
          setView("liste");
        }}
        className={`inline-flex items-center gap-1.5 px-4 py-2 ${view === "liste" ? "bg-indigo-600 text-white" : "text-gray-700"}`}
      >
        <LayoutList className="h-4 w-4" aria-hidden="true" />
        {t("dealer.prospects.vueListe")}
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={view === "colonnes"}
        data-testid="dealer-prospects-vue-colonnes"
        onClick={() => {
          setView("colonnes");
        }}
        className={`inline-flex items-center gap-1.5 px-4 py-2 ${view === "colonnes" ? "bg-indigo-600 text-white" : "text-gray-700"}`}
      >
        <Columns3 className="h-4 w-4" aria-hidden="true" />
        {t("dealer.prospects.vueColonnes")}
      </button>
    </div>
  );

  const column = (key: string, label: string, body: ReactElement, count?: number): ReactElement => (
    <section
      key={key}
      aria-label={label}
      data-testid="dealer-prospects-colonne"
      className="rounded-2xl border border-gray-200 bg-gray-50 p-3"
    >
      <h2 className="m-0 mb-2 text-sm font-semibold text-gray-900">
        {label}
        {count === undefined ? null : (
          <>
            {" "}
            <span
              className="rounded-full bg-white px-2 py-0.5 text-xs"
              data-testid="dealer-prospects-colonne-compte"
            >
              {String(count)}
            </span>
          </>
        )}
      </h2>
      {body}
    </section>
  );

  const waits = (words: string): ReactElement => <p className="m-0 text-sm text-gray-500">{words}</p>;

  /** Only cars with a save (or a refused read) get a row, in the vehicle list's order; the note says views are not counted. */
  const interestBody = (): ReactElement => {
    if (interest.state === "refused") {
      return (
        <p
          role="alert"
          className={`m-0 text-sm ${ERROR_TEXT}`}
          data-testid="dealer-prospects-interet-refus-liste"
        >
          {interest.message}
        </p>
      );
    }
    if (interest.state === "none") return waits(t("dealer.prospects.regardVide"));
    const shown = interest.lines.filter((line) => !line.ok || line.saved >= 1);
    if (shown.length === 0) return waits(t("dealer.prospects.regardVide"));
    return (
      <div>
        <ul className="m-0 grid list-none gap-1.5 p-0">
          {shown.map((line) =>
            line.ok ? (
              <li
                key={line.id}
                className="flex items-baseline justify-between gap-3 text-sm text-gray-900"
                data-testid="dealer-prospects-interet-ligne"
              >
                <span data-testid="dealer-prospects-interet-nom">{line.title}</span>
                <span className="whitespace-nowrap tabular-nums text-gray-700">
                  {pl(
                    line.saved,
                    t("dealer.prospects.enregistrement"),
                    t("dealer.prospects.enregistrements"),
                  )}
                </span>
              </li>
            ) : (
              <li
                key={line.id}
                className="text-sm text-gray-900"
                data-testid="dealer-prospects-interet-ligne"
              >
                <span className="block" data-testid="dealer-prospects-interet-nom">
                  {line.title}
                </span>
                <span
                  role="alert"
                  className={`mt-0.5 block ${ERROR_TEXT}`}
                  data-testid="dealer-prospects-interet-refus"
                >
                  {line.message}
                </span>
              </li>
            ),
          )}
        </ul>
        <small className="mt-2 block text-sm text-gray-500" data-testid="dealer-prospects-interet-note">
          {t("dealer.prospects.consultationsNote")}
        </small>
      </div>
    );
  };

  return (
    <section>
      {title}
      <p className="m-0 mt-1 text-sm text-gray-500" data-testid="dealer-prospects-sous-titre">
        {t("dealer.prospects.sousTitre")}
      </p>

      {view === "colonnes" || leads.length > 0 ? (
        <div className="mt-5 flex items-center gap-3">
          <span className="text-sm text-gray-500" data-testid="dealer-prospects-compte">
            {pl(leads.length, t("dealer.prospects.personne"), t("dealer.prospects.personnes"))}
          </span>
          <span className="flex-1" />
          {views}
        </div>
      ) : (
        <div className="mt-5 flex justify-end">{views}</div>
      )}

      {view === "colonnes" ? (
        <div className="mt-4 grid gap-3 md:grid-cols-3" data-testid="dealer-prospects-colonnes">
          {column("regard", t("dealer.prospects.voituresEnregistrees"), interestBody())}
          {column("visite", t("dealer.prospects.colonneVisite"), waits(t("dealer.prospects.visiteVide")))}
          {column(
            "financement",
            t("dealer.prospects.colonneFinancement"),
            leads.length === 0 ? (
              waits(t("dealer.prospects.colonneVide"))
            ) : (
              <div className="grid gap-2">{leads.map((one) => card(one, true))}</div>
            ),
            leads.length === 0 ? undefined : leads.length,
          )}
        </div>
      ) : leads.length === 0 ? (
        <div
          className="mt-4 rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center"
          data-testid="dealer-prospects-vide"
        >
          <h2 className="text-lg font-semibold text-gray-900">{t("dealer.prospects.vide")}</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">{t("dealer.prospects.videCorps")}</p>
        </div>
      ) : (
        <div className="mt-4 grid gap-2.5" data-testid="dealer-prospects-liste">
          {leads.map((one) => card(one, false))}
        </div>
      )}

      {view === "liste" ? (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <section
            className="rounded-2xl border border-gray-200 bg-white p-4"
            data-testid="dealer-prospects-anonyme"
          >
            <h2 className="m-0 text-base font-semibold text-gray-900">
              {t("dealer.prospects.voituresEnregistrees")}
            </h2>
            <small className="block text-xs text-gray-500">{t("dealer.prospects.anonymeNote")}</small>
            <div className="mt-2">{interestBody()}</div>
          </section>
          <section
            className="rounded-2xl border border-gray-200 bg-white p-4"
            data-testid="dealer-prospects-visite"
          >
            <h2 className="m-0 text-base font-semibold text-gray-900">
              {t("dealer.prospects.colonneVisite")}
            </h2>
            <div className="mt-2">{waits(t("dealer.prospects.visiteVide"))}</div>
          </section>
        </div>
      ) : null}
    </section>
  );
}
